class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!this.poster) return;
    
    this.poster.addEventListener('click', this.loadContent.bind(this));
  }

  pauseIframe(iframe) {
    try {
      const src = iframe.src || '';
      if (!src) return;

      if (src.includes('youtube.com') || src.includes('youtu.be') || src.includes('youtube-nocookie.com')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (src.includes('vimeo.com')) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    } catch (error) {
      // Ignore cross-origin player messaging errors.
    }
  }

  normalizeIframeSrcForApi(iframe) {
    try {
      const src = iframe.getAttribute('src') || '';
      if (!src) return;

      const url = new URL(src, window.location.origin);
      const host = url.hostname;
      let changed = false;

      if (
        host.includes('youtube.com')
        || host.includes('youtu.be')
        || host.includes('youtube-nocookie.com')
      ) {
        if (url.searchParams.get('enablejsapi') !== '1') {
          url.searchParams.set('enablejsapi', '1');
          changed = true;
        }

        if (url.searchParams.get('playsinline') !== '1') {
          url.searchParams.set('playsinline', '1');
          changed = true;
        }

        if (url.searchParams.get('origin') !== window.location.origin) {
          url.searchParams.set('origin', window.location.origin);
          changed = true;
        }
      } else if (host.includes('vimeo.com')) {
        if (url.searchParams.get('api') !== '1') {
          url.searchParams.set('api', '1');
          changed = true;
        }
      }

      if (changed) {
        iframe.setAttribute('src', url.toString());
      }
    } catch (error) {
      // Ignore invalid URL parsing or blocked URL mutations.
    }
  }

  playIframe(iframe) {
    try {
      const src = iframe.src || '';
      if (!src) return;

      if (src.includes('youtube.com') || src.includes('youtu.be') || src.includes('youtube-nocookie.com')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      } else if (src.includes('vimeo.com')) {
        iframe.contentWindow?.postMessage('{"method":"play"}', '*');
      }
    } catch (error) {
      // Ignore cross-origin player messaging errors.
    }
  }

  requestIframePlay(iframe) {
    if (!iframe) return;
    this.playIframe(iframe);
    window.setTimeout(() => this.playIframe(iframe), 250);
  }

  pauseAllMediaExcept(currentMedia = null) {
    document.querySelectorAll('video, iframe, model-viewer, product-model').forEach((element) => {
      if (currentMedia && element === currentMedia) return;

      if (element.tagName === 'VIDEO') {
        if (!element.paused) {
          element.pause();
        }
        return;
      }

      if (element.tagName === 'IFRAME') {
        this.pauseIframe(element);
        return;
      }

      if (element.matches('model-viewer, product-model')) {
        if (element.tagName === 'PRODUCT-MODEL' && element.modelViewerUI) {
          element.modelViewerUI.pause();
        } else if (typeof element.pause === 'function') {
          element.pause();
        }
      }
    });
  }

  pauseSiblingsMedia(currentMedia = null, fromPlayEvent = false) {
    if (!fromPlayEvent && !currentMedia && typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }

    this.pauseAllMediaExcept(currentMedia);
  }

  attachMediaListeners(mediaElement) {
    if (mediaElement.nodeName === 'VIDEO') {
      mediaElement.addEventListener('play', () => {
        this.pauseSiblingsMedia(mediaElement, true);
      });
      return;
    }

    if (mediaElement.nodeName === 'IFRAME') {
      const pauseSiblings = () => this.pauseSiblingsMedia(mediaElement, true);
      mediaElement.addEventListener('focus', pauseSiblings);
      mediaElement.addEventListener('pointerdown', pauseSiblings);
      mediaElement.addEventListener('touchstart', pauseSiblings, { passive: true });

      // Clicking inside cross-origin iframe often triggers window blur only.
      window.addEventListener('blur', () => {
        if (document.activeElement === mediaElement) {
          pauseSiblings();
        }
      });
    }
  }

  loadContent(focus = true) {
    let deferredElement = this.querySelector('video, model-viewer, iframe');

    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      const template = this.querySelector('template');
      
      if (template) {
        content.appendChild(template.content.firstElementChild.cloneNode(true));
        this.setAttribute('loaded', true);
        
        const mediaElement = content.querySelector('video, model-viewer, iframe');
        if (!mediaElement) {
          console.warn('DeferredMedia: No video, model-viewer, or iframe element found in template');
          return;
        }
        
        deferredElement = this.appendChild(mediaElement);
        if (deferredElement.nodeName === 'IFRAME') {
          this.normalizeIframeSrcForApi(deferredElement);
          deferredElement.addEventListener('load', () => this.requestIframePlay(deferredElement), { once: true });
        }
        this.attachMediaListeners(deferredElement);
        
        if (focus) {
          deferredElement.focus();
        }
        
        if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          deferredElement.play().catch(() => {
            console.log('Autoplay prevented by browser');
          });
        } else if (deferredElement.nodeName === 'IFRAME') {
          this.requestIframePlay(deferredElement);
        }
        
        const formerStyle = deferredElement.getAttribute('style');
        deferredElement.setAttribute('style', 'display: block;');
        window.setTimeout(() => {
          if (formerStyle !== null) {
            deferredElement.setAttribute('style', formerStyle);
          } else {
            deferredElement.removeAttribute('style');
          }
        }, 0);
      }
    } else if (!deferredElement) {
      return;
    }

    this.pauseSiblingsMedia(deferredElement, false);

    if (focus && typeof deferredElement.focus === 'function') {
      deferredElement.focus();
    }

    if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute('autoplay')) {
      deferredElement.play().catch(() => {
        console.log('Autoplay prevented by browser');
      });
    } else if (deferredElement.nodeName === 'IFRAME') {
      this.requestIframePlay(deferredElement);
    }
  }

  pauseAllMedia() {
    document.querySelectorAll('video').forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
    
    document.querySelectorAll('model-viewer').forEach(model => {
      if (model.pause) {
        model.pause();
      }
    });
  }
}

class ProductModel extends DeferredMedia {
  constructor() {
    super();
  }

  loadContent() {
    super.loadContent();
    
    // Load Shopify model viewer features
    if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    }
  }

  setupModelViewerUI(errors) {
    if (errors) return;
    
    const modelViewer = this.querySelector('model-viewer');
    if (modelViewer && window.Shopify && window.Shopify.ModelViewerUI) {
      this.modelViewerUI = new window.Shopify.ModelViewerUI(modelViewer);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}

window.pauseAllMedia = function() {
  document.querySelectorAll('video').forEach(video => {
    if (!video.paused) {
      video.pause();
    }
  });

  document.querySelectorAll('iframe').forEach((iframe) => {
    try {
      if (iframe.src.includes('youtube.com') || iframe.src.includes('youtu.be') || iframe.src.includes('youtube-nocookie.com')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (iframe.src.includes('vimeo.com')) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    } catch (error) {
      // Ignore cross-origin player messaging errors.
    }
  });
  
  document.querySelectorAll('model-viewer').forEach(model => {
    if (model.pause) {
      model.pause();
    }
  });
};

window.DeferredMedia = DeferredMedia;

window.ProductModel = window.ProductModel || {};
window.ProductModel.loadShopifyXR = function() {
  if (!window.Shopify || !window.Shopify.loadFeatures) return;

  window.Shopify.loadFeatures([
    {
      name: 'shopify-xr',
      version: '1.0',
      onLoad: window.ProductModel.setupShopifyXR,
    },
  ]);
};

window.ProductModel.setupShopifyXR = function(errors) {
  if (errors) return;

  if (!window.ShopifyXR) {
    document.addEventListener('shopify_xr_initialized', () => window.ProductModel.setupShopifyXR());
    return;
  }

  document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
    window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
    modelJSON.remove();
  });
  window.ShopifyXR.setupXRElements();
};

window.addEventListener('DOMContentLoaded', () => {
  if (window.ProductModel && window.ProductModel.loadShopifyXR) {
    window.ProductModel.loadShopifyXR();
  }
});
